import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, TextField } from "@mui/material";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useGlobalModal } from "../../../components/Contexts/GlobalModalContext";
import { ModalActions } from "../../../components/Modals/ModalActions";
import { Button } from "../../../components/Buttons/Button";
import { DiggingLocationDTO } from "../../../DTOs/Production/DiggingLocationDTO";
import i18n from "../../../i18n";

type MoveLoadingRecordModalProps = {
  availableLocations: DiggingLocationDTO[];
  currentLocation: DiggingLocationDTO;
  onMove: (newDiggingPatternId: number) => Promise<boolean>;
}

const MoveLoadingRecordModal: React.FC<MoveLoadingRecordModalProps> = ({
  availableLocations,
  currentLocation,
  onMove
}) => {
  const { t } = useTranslation("production");
  const { hideModal } = useGlobalModal();
  const [isWorking, setIsWorking] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<number>(0);

  return (
    <form>
      <h2 className="border-b border-theme-border font-semibold pb-2 text-[1.6rem] text-theme-text">
        {t("moveModal.title")}
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          className={`text-theme-text text-sm transition ease-out mb-2 ml-1 opacity-${isWorking ? 100 : 0}`}
        />
      </h2>
      <div className="pt-2 mb-2">
        <p className="text-sm text-theme-text mb-1"><Trans t={t} i18nKey="moveModal.from" values={{ blastMasterId: currentLocation.blastMasterId ?? currentLocation.patternBlastId, pit: currentLocation.pit }} components={{ bold: <span className="font-semibold" /> }} /></p>
      </div>

      <div className="flex flex-col gap-1 mb-4">
        <p className="text-sm text-theme-text"><span className="font-semibold">{t("moveModal.to")}</span></p>
        <div className="flex flex-col">
          <Autocomplete<DiggingLocationDTO, false, false, false>
            classes={{ root: "w-full" }}
            size="small"
            options={availableLocations}
            value={availableLocations.find(o => o.diggingPatternId === selectedLocationId) ?? null}
            getOptionLabel={(o) => t("moveModal.optionLabel", { blastMasterId: o.blastMasterId ?? o.patternBlastId, pit: o.pit })}
            renderOption={(props, o) => {
              return (
                <li {...props} key={o.diggingPatternId} className="p-2 cursor-pointer hover:bg-theme-surface transition duration-150">
                  <Trans t={t} i18nKey="moveModal.option" values={{ blastMasterId: o.blastMasterId ?? o.patternBlastId, pit: o.pit }} components={{ bold: <span className="font-semibold" /> }} />
                </li>
              )
            }}
            isOptionEqualToValue={(opt, val) => opt.diggingPatternId === val.diggingPatternId}
            onChange={(_, opt) => setSelectedLocationId(opt?.diggingPatternId ?? 0)}
            noOptionsText={t("moveModal.noOtherLocations")}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("moveModal.selectBlastmaster")}
                size="small"
              />
            )}
          />
        </div>
      </div>

      <ModalActions className="!mt-12">
        <Button variant="muted" className="!text-theme-text" onClick={hideModal}>
          {i18n.t("actions.cancel")}
        </Button>
        <Button
          variant="info"
          isDefaultAction
          mnemonic="m"
          disabled={selectedLocationId === 0}
          onClick={async () => {
            if (selectedLocationId === 0) {
              return;
            }
            setIsWorking(true);
            const success = await onMove(selectedLocationId);
            if (success) {
              hideModal();
            }
            setIsWorking(false);
          }}
        >
          {t("trucksTable.move")}
        </Button>
      </ModalActions>
    </form>
  );
};

export default MoveLoadingRecordModal;